import React, { useEffect, useState, useRef } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, btn, btnGhost } from "../drawings/_drawingsCommon";

const SAMPLE_PROMPTS = [
  "what pipe class should I use for 98% sulfuric acid at 40 °C?",
  "add a 2-inch drain with block valve at the low point of line 6in-PL-005",
  "find all vessels without PSV",
  "list all lines without insulation that operate above 60 °C",
  "what is the minimum distance between the fired heater and the column per API?",
  "add a level control loop on V-201 with DCS controller and pneumatic CV",
];

export default function CommandBarPanel({ projectId }) {
  const [cmd, setCmd] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");
  const inputRef = useRef();

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/ai/commands`, { params: { limit: 20 } });
      setHistory(data || []);
    } catch { /* empty */ }
  };
  useEffect(() => { loadHistory(); }, [projectId]);

  const send = async (text) => {
    const c = (text ?? cmd).trim();
    if (!c) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/command`,
        { command: c, context_kind: "p&id" });
      setResult(data);
      setCmd("");
      loadHistory();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally { setBusy(false); inputRef.current?.focus(); }
  };

  return (
    <div data-testid="cmd-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={card}>
        <div style={{ ...label, marginBottom: 6 }}>Command Bar — Capexara Process Industry AI</div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ color: "#F26B1F", fontWeight: 800, fontFamily: "monospace" }}>›</span>
          <input ref={inputRef} data-testid="cmd-input"
            placeholder='Type a natural-language design command (e.g. "add bypass around TCV-201")'
            value={cmd} onChange={(e) => setCmd(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && send()}
            style={{ flex: 1, padding: "8px 10px", fontSize: 12, fontFamily: "monospace",
                     background: "var(--pf-surface-2)", color: "var(--pf-fg)",
                     border: "1px solid var(--pf-border)", borderRadius: 4 }} />
          <button style={btn} data-testid="cmd-send" onClick={() => send()} disabled={busy || !cmd.trim()}>
            {busy ? "Thinking…" : "Send"}
          </button>
        </div>
        <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
          {SAMPLE_PROMPTS.map((p, i) => (
            <button key={i} data-testid={`cmd-sample-${i}`} style={{ ...btnGhost, padding: "3px 8px", fontSize: 10 }}
              onClick={() => { setCmd(p); inputRef.current?.focus(); }}>{p.slice(0, 40)}…</button>
          ))}
        </div>
      </div>

      {err && <div style={{ padding: 8, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4, fontSize: 12 }}>{err}</div>}

      {result && <ResultCard r={result} />}

      <div style={card}>
        <div style={{ ...label, marginBottom: 6 }}>Recent Commands · {history.length}</div>
        {history.length === 0 ? (
          <div style={{ color: "var(--pf-muted)", fontSize: 11, textAlign: "center", padding: 12 }}>
            No history yet — try a sample prompt above.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 280, overflowY: "auto" }}>
            {history.map((h, i) => (
              <button key={i} data-testid={`cmd-hist-${i}`} style={{ ...btnGhost, justifyContent: "flex-start", textAlign: "left", padding: "6px 8px" }}
                onClick={() => setResult(h.result)}>
                <span style={{ color: "#F26B1F", fontFamily: "monospace", marginRight: 6 }}>›</span>
                {h.command}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ResultCard({ r }) {
  return (
    <div style={card} data-testid="cmd-result">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={label}>AI Response</div>
        <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 3,
                       background: "rgba(242,107,31,0.15)", color: "#F26B1F", fontWeight: 700,
                       textTransform: "uppercase" }}>{r.intent || "response"}</span>
      </div>
      {r.interpretation && (
        <div style={{ fontSize: 12, marginBottom: 8 }}>
          <strong>Interpretation:</strong> {r.interpretation}
        </div>
      )}
      {r.answer && (
        <div style={{ marginTop: 6, padding: 10, background: "var(--pf-surface-2)", borderRadius: 4, fontSize: 12, lineHeight: 1.5 }}>
          {r.answer}
        </div>
      )}
      {r.ops && r.ops.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={label}>Proposed Operations · {r.ops.length}</div>
          {r.ops.map((op, i) => (
            <div key={i} style={{ marginTop: 6, padding: 8, background: "var(--pf-surface-2)", borderRadius: 4, fontSize: 11 }}>
              <div><strong style={{ color: "#F26B1F" }}>{op.action}</strong> → {op.target}</div>
              {op.rationale && <div style={{ color: "var(--pf-muted)", marginTop: 2 }}>{op.rationale}</div>}
            </div>
          ))}
        </div>
      )}
      {r.warnings && r.warnings.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ ...label, color: "#f59e0b" }}>Warnings</div>
          <ul style={{ paddingLeft: 18, margin: 4, fontSize: 11, color: "#f59e0b" }}>
            {r.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
      {r.standards && r.standards.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 10, color: "var(--pf-muted)" }}>
          <strong>Standards:</strong> {r.standards.join(" · ")}
        </div>
      )}
      {r.raw && !r.ops && !r.answer && (
        <pre style={{ marginTop: 6, padding: 8, background: "var(--pf-surface-2)", borderRadius: 4, fontSize: 10, fontFamily: "monospace", whiteSpace: "pre-wrap" }}>{r.raw}</pre>
      )}
    </div>
  );
}
